import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, WifiOff } from 'lucide-react';
import ConfirmDialog from '../../projects/components/ConfirmDialog';
import { extractErrorMessage } from '../../../shared/utils/extractErrorMessage';
import useAutoClose from '../../../shared/hooks/useAutoClose';
import {
  MEMBER_FILTERS,
  MEMBER_STATUS,
  TERMINAL_STATUSES,
  useBatchAggregate,
  useBatchFilterCounts,
  useBatchMembers,
  useBatchMeta,
  useBatchRunActions,
  useStreamDegraded,
} from '../stores/batchRunStore';
import { formatClock, formatLocalTime } from '../utils/suiteFormatters';
import useProjectDisplayName from '../hooks/useProjectDisplayName';
import BatchProgressBar from './BatchProgressBar';
import ConsolidatedReport from './ConsolidatedReport';
import '../../automationScripts/styles/RunProgressBar.css';
import '../styles/ExecutionStudio.css';

/** A member that is still going — the stop button only appears on these. */
const isStoppable = (status) => ![
  MEMBER_STATUS.PASSED, MEMBER_STATUS.PARTIAL, MEMBER_STATUS.FAILED,
  MEMBER_STATUS.CANCELLED, MEMBER_STATUS.OUT_OF_SCOPE, MEMBER_STATUS.NOT_DISPATCHED,
  MEMBER_STATUS.SUBMIT_FAILED, MEMBER_STATUS.SUBMIT_UNCERTAIN,
].includes(status);

const isLive = (status) => status === MEMBER_STATUS.RUNNING || status === MEMBER_STATUS.CANCELLING;

/** Milliseconds between two instants, or up to now while still open. */
const msBetween = (start, end, now) => {
  if (!start) return 0;
  const from = new Date(start).getTime();
  if (Number.isNaN(from)) return 0;
  const to = end ? new Date(end).getTime() : now;
  return Math.max(0, to - from);
};

/**
 * Live batch run window (Figma `11-run-window-with-details`).
 *
 * Built like the single run: a bar docked to the bottom of the screen, with
 * the run details in a panel that opens upwards from it when Details is
 * clicked. The consolidated report joins the details once the batch has ended.
 *
 * Script rows do not expand. The batch holds one websocket for the whole run —
 * there is no per-script stream to show, so a dropdown would open onto nothing.
 * Nor can one script be stopped on its own: cancelling is for the whole batch.
 *
 * Deliberately absent: no ETA / "time remaining" and no concurrency cap —
 * neither is reported, so neither is stubbed or given reserved space.
 *
 * Per TONIC-PEX-IMPL-FE-001 Task 6.
 */
const BatchRunTracker = ({
  onCancelBatch, onClose,
  report, reportLoading, reportError, onRetryReport, onDownloadReport, reportFormats, downloadingReport,
}) => {
  const meta = useBatchMeta();
  const rows = useBatchMembers();
  const aggregate = useBatchAggregate();
  const filterCounts = useBatchFilterCounts();
  const degraded = useStreamDegraded();
  const { setFilter } = useBatchRunActions();
  const projectDisplayName = useProjectDisplayName(meta.projectId, meta.projectName);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null); // { stopping, finished }
  const [cancelling, setCancelling] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelError, setCancelError] = useState('');

  const running = aggregate.complete < aggregate.scripts;

  // A finished batch closes itself after a short countdown. Any click inside the
  // window keeps it open — the user is reading it, or picking a report format.
  const { secondsLeft: autoCloseIn, keepOpen } = useAutoClose(!running && aggregate.scripts > 0, onClose);

  // Elapsed clocks tick while anything is still going
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  /**
   * A request that never got a response did not reach the service — that points
   * at the route or CORS, not at the run. Saying "Network Error" sends the
   * reader looking in the wrong place.
   */
  const describeFailure = (err) => (err?.response
    ? extractErrorMessage(err, 'The cancel request was rejected.')
    : 'Could not reach the execution service — nothing was cancelled, and the run is still going.');

  const stoppableCount = useMemo(() => rows.filter(r => isStoppable(r.status)).length, [rows]);

  /**
   * The single-run cancel rules, applied to the batch. A batch is cancelled as
   * a whole — there is no per-script stop — and ends in the cancelled state:
   * orange, labelled "Cancelled", no pass/fail verdict on the bar.
   */
  const batchCancelled = !running && (
    meta.batchStatus === 'CANCELLED' || rows.some(r => r.status === MEMBER_STATUS.CANCELLED)
  );

  // Scripts that ended by finishing. A cancelled one never counts as progress,
  // so the batch bar stops where the work actually got to.
  const progressed = useMemo(
    () => rows.filter(r => r.status !== MEMBER_STATUS.CANCELLED && TERMINAL_STATUSES.has(r.status)).length,
    [rows]
  );

  /**
   * Where each script's bar had got to while it was still running. The engine
   * reports a cancelled run as fully tidied up; showing that as a full bar says
   * the work completed. The last live width is where it actually stopped.
   */
  const lastLivePctRef = useRef({});
  const hasFailures = aggregate.failed > 0
    || rows.some(r => r.status === MEMBER_STATUS.FAILED || r.status === MEMBER_STATUS.SUBMIT_FAILED);

  /**
   * Nothing left to stop. The cancel dialog can be open when this flips (the
   * batch finished while the user was deciding, or the request is still in
   * flight) — it then turns into a plain notice with a Close button, so the
   * user is never stuck behind a disabled "Cancelling..." or a refusal.
   */
  const alreadyFinished = !running || stoppableCount === 0;

  const closeCancelDialog = () => { setConfirmCancel(null); setCancelError(''); };

  const askCancel = () => setConfirmCancel({
    stopping: stoppableCount,
    finished: rows.length - stoppableCount,
  });

  const handleConfirmCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const result = await onCancelBatch();
      // Report what actually happened, not "all stopped"
      const affected = result?.affected ?? confirmCancel.stopping;
      const already = result?.alreadyTerminal ?? confirmCancel.finished;
      setCancelNote(
        already > 0
          ? `Cancelling ${affected} — ${already} already finished.`
          : `Cancelling ${affected}.`
      );
      setConfirmCancel(null);
    } catch (err) {
      // Keep the dialog open with the reason. A cancel that fails is exactly
      // when the user needs to know, so this must never reach the error overlay.
      setCancelError(describeFailure(err));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      className={[
        'mock-run-bottom',
        'tsg-run-dock',
        running ? 'mock-run-bottom--running' : '',
        !running && !batchCancelled ? 'mock-run-bottom--completed' : '',
        batchCancelled ? 'mock-run-bottom--cancelled' : '',
        !running && hasFailures && !batchCancelled ? 'mock-run-bottom--failed' : '',
      ].filter(Boolean).join(' ')}
      role="region"
      aria-label="Batch run progress"
      onPointerDown={keepOpen}
    >
      <BatchProgressBar
        running={running}
        cancelled={batchCancelled}
        hasFailures={hasFailures}
        complete={aggregate.complete}
        progressed={progressed}
        scripts={aggregate.scripts}
        passed={aggregate.passed}
        failed={aggregate.failed}
        isDetailsOpen={detailsOpen}
        onToggleDetails={() => setDetailsOpen(open => !open)}
        onCancel={askCancel}
        cancelDisabled={stoppableCount === 0}
        isCancelling={rows.some(r => r.status === MEMBER_STATUS.CANCELLING)}
        onClose={onClose}
        autoCloseIn={autoCloseIn}
        onKeepOpen={keepOpen}
      />

      {/* Opens upwards: the dock is pinned to the bottom, so growing this
          panel pushes the bar up, exactly as the single run's does. */}
      <div className={`mock-run-details tsg-run-details${detailsOpen ? ' mock-run-details--open' : ''}`}>
        <div className="tsg-run-details__scroll">
          <h2 className="tsg-run-details__title">Run Details</h2>

          <div className="tsg-run-details__chips">
            <span className="tsg-run-chip">{meta.groupingName}</span>
            {projectDisplayName && <span className="tsg-run-chip">{projectDisplayName}</span>}
          </div>

          <div className="tsg-run-banner">
            <span className="tsg-run-banner__pill">RUN</span>
            <span className="tsg-run-banner__lead">
              {aggregate.complete} of {aggregate.scripts} scripts complete
            </span>
            <span className="tsg-run-banner__sep" aria-hidden="true">|</span>
            <span className="tsg-run-banner__meta">
              {formatClock(msBetween(meta.startedAt, meta.batchEndedAt, now))} elapsed
            </span>
          </div>

          {degraded && (
            <div className="tsg-alert tsg-alert--warn" role="status">
              <WifiOff size={16} />
              <span>Live updates are degraded — falling back to polling. Figures may lag by a few seconds.</span>
            </div>
          )}

          {cancelNote && (
            <div className="tsg-alert" role="status">
              <AlertTriangle size={16} />
              <span>{cancelNote}</span>
            </div>
          )}

          {cancelError && !confirmCancel && !alreadyFinished && (
            <div className="tsg-alert tsg-alert--warn" role="alert">
              <AlertTriangle size={16} />
              <span>{cancelError}</span>
            </div>
          )}

          {/* Permutation totals — summed from the member rows */}
          <div className="tsg-run-stats">
            <div className="tsg-run-stat">
              <span className="tsg-run-stat__label">TOTAL PERMUTATIONS</span>
              <strong className="tsg-run-stat__value">
                {aggregate.total}
              </strong>
              <span className="tsg-run-stat__sub">{aggregate.scripts} script{aggregate.scripts === 1 ? '' : 's'}</span>
            </div>
            <div className="tsg-run-stat">
              <span className="tsg-run-stat__label">EXECUTED</span>
              <strong className="tsg-run-stat__value">{aggregate.executed}</strong>
            </div>
            <div className="tsg-run-stat">
              <span className="tsg-run-stat__label">PASSED</span>
              <strong className="tsg-run-stat__value tsg-run-stat__value--pass">{aggregate.passed}</strong>
            </div>
            <div className="tsg-run-stat">
              <span className="tsg-run-stat__label">FAILED</span>
              <strong className="tsg-run-stat__value tsg-run-stat__value--fail">{aggregate.failed}</strong>
            </div>
          </div>

          {/* The consolidated report only exists once the batch has ended. Its
              figures come from the summary; its download is the stored report. */}
          {!running && (
            <ConsolidatedReport
              report={report}
              projectId={meta.projectId}
              loading={reportLoading}
              onDownloadReport={onDownloadReport}
              reportFormats={reportFormats}
              downloading={downloadingReport}
              showMembers={false}
            />
          )}
          {!running && reportError && !report && (
            <div className="tsg-alert" role="alert">
              <AlertTriangle size={16} />
              <span>
                {reportError}{' '}
                {onRetryReport && <button className="tsg-link-btn" onClick={onRetryReport}>Try again</button>}
              </span>
            </div>
          )}

          <div className="tsg-run-filters" role="tablist" aria-label="Filter scripts by status">
            <span className="tsg-run-filters__label">Filter:</span>
            {MEMBER_FILTERS.map((name) => (
              <button
                key={name}
                role="tab"
                aria-selected={meta.filter === name}
                className={`tsg-run-filter${meta.filter === name ? ' tsg-run-filter--active' : ''}`}
                onClick={() => setFilter(name)}
              >
                {name} {filterCounts[name]}
              </button>
            ))}
          </div>

          <div className="tsg-run-rows">
            {rows.length === 0 ? (
              <p className="tsg-empty-inline">No scripts match this filter.</p>
            ) : rows.map((row) => {
              const total = row.permutations.total;
              const executed = row.permutations.executed;
              // A running script may not know its total yet — render `?`, never 0
              const ratio = `${executed}/${total == null ? '?' : total}`;
              const passPct = total ? Math.round((row.permutations.passed / total) * 100) : 0;
              const failPct = total ? Math.round((row.permutations.failed / total) * 100) : 0;
              const live = isLive(row.status);
              const cancelled = row.status === MEMBER_STATUS.CANCELLED;
              // With no known total, a live row shows motion rather than a false fill
              const livePct = total ? Math.round((executed / total) * 100) : 30;
              if (live && row.runId) lastLivePctRef.current[row.runId] = livePct;
              // Stopped where it was — never filled out by the cancel
              const stoppedPct = lastLivePctRef.current[row.runId]
                ?? (total ? Math.round((executed / total) * 100) : 0);

              return (
                <div key={row.runId || row.name} className="tsg-run-row-card" role="group" aria-label={row.name}>
                  <div className="tsg-run-row-card__main">
                    <span className="tsg-run-row-card__order">{row.order}</span>
                    <span className="tsg-run-row-card__name" title={row.name}>
                      {row.name}
                      {live && <span className="tsg-run-row-card__live" aria-hidden="true" />}
                    </span>
                    <span className={`tsg-status-pill tsg-status-pill--${row.status.toLowerCase()}`}>{row.status}</span>
                    <span className="tsg-run-row-card__count">{ratio}</span>
                    <span className={`tsg-run-row-card__bar${live ? ' tsg-run-row-card__bar--live' : ''}`}>
                      {live ? (
                        <span
                          className={`tsg-run-row-card__bar-live${row.status === MEMBER_STATUS.CANCELLING ? ' tsg-run-row-card__bar-live--stopping' : ''}`}
                          style={{ width: `${livePct}%` }}
                        />
                      ) : cancelled ? (
                        <span
                          className="tsg-run-row-card__bar-cancelled"
                          style={{ width: `${stoppedPct}%` }}
                          aria-label={`Cancelled at ${stoppedPct}%`}
                        />
                      ) : (
                        <>
                          <span className="tsg-run-row-card__bar-pass" style={{ width: `${passPct}%` }} />
                          <span className="tsg-run-row-card__bar-fail" style={{ width: `${failPct}%` }} />
                        </>
                      )}
                    </span>
                    <span className="tsg-run-row-card__clock">
                      {/* No start = unknown, not zero: a queued row, or one that
                          finished before we ever saw it running */}
                      {row.startedAt ? formatClock(msBetween(row.startedAt, row.endedAt, now)) : '—'}
                    </span>
                  </div>

                  {(row.status === MEMBER_STATUS.SUBMIT_FAILED
                    || row.status === MEMBER_STATUS.NOT_DISPATCHED
                    || row.status === MEMBER_STATUS.SUBMIT_UNCERTAIN) && (
                    // A member failing to dispatch is a row state, never a page error (BR-33)
                    <p className="tsg-tracker__row-error">
                      {row.status === MEMBER_STATUS.SUBMIT_UNCERTAIN
                        ? 'It is not known whether this script was sent — it may already be running, so it is not offered for re-run.'
                        : 'Could not be dispatched.'}
                      {(row.failureReason || row.submitError) && ` ${row.failureReason || row.submitError}`}
                      {' '}The rest of the batch is unaffected.
                    </p>
                  )}

                  {/* Why it stopped. CANCELLED on its own raises the question and
                      does not answer it. */}
                  {row.status === MEMBER_STATUS.CANCELLED && row.failureReason && (
                    <p className="tsg-tracker__row-note">{row.failureReason}</p>
                  )}
                </div>
              );
            })}
          </div>

          {meta.startedAt && (
            // The viewer's system time, with its zone label (BR-42)
            <p className="tsg-tracker__started">Started {formatLocalTime(meta.startedAt)}</p>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmCancel}
        title={alreadyFinished ? 'This run has already finished' : 'Cancel this run?'}
        message={
          alreadyFinished
            ? 'Every script has finished, so there is nothing left to cancel.'
            : confirmCancel
            ? `${confirmCancel.stopping} script${confirmCancel.stopping === 1 ? '' : 's'} will stop.` +
              (confirmCancel.finished > 0 ? ` ${confirmCancel.finished} already finished and will be left as they are.` : '')
            : ''
        }
        confirmLabel="Cancel run"
        cancelLabel={alreadyFinished ? 'Close' : 'Keep running'}
        onConfirm={handleConfirmCancel}
        onCancel={closeCancelDialog}
        loading={cancelling && !alreadyFinished}
        loadingLabel="Cancelling..."
        errorMessage={alreadyFinished ? '' : cancelError}
        hideConfirm={alreadyFinished}
      />
    </div>
  );
};

BatchRunTracker.propTypes = {
  onCancelBatch: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  /** consolidated-summary.json — shown in the details once the batch has ended. */
  report: PropTypes.object,
  reportLoading: PropTypes.bool,
  reportError: PropTypes.string,
  onRetryReport: PropTypes.func,
  /** Downloads the Core's stored report for this run, as 'html' or 'pdf'. */
  onDownloadReport: PropTypes.func,
  reportFormats: PropTypes.arrayOf(PropTypes.string),
  downloadingReport: PropTypes.bool,
};

BatchRunTracker.defaultProps = {
  onClose: () => {},
  report: null,
  reportLoading: false,
  reportError: '',
  onRetryReport: null,
  onDownloadReport: null,
  reportFormats: ['html'],
  downloadingReport: false,
};

export default BatchRunTracker;
